// =============================================================================
// Vault Studio (/vault) — the memory (raw / wiki / output) — newspaper theme
// =============================================================================

import { stateful, useState, useEffect } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { listVaultTree, readVaultDoc } from '../../db/analytics-resolvers.js';
import { newspaperStyle, masthead, NP } from './newspaper.js';

interface VaultFolder {
  name: string;
  index: string;
  files: string[];
}
interface VaultTree {
  rootIndex: string;
  folders: VaultFolder[];
}

function renderInline(text: string) {
  const parts: unknown[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(html`<strong>${tok.slice(2, -2)}</strong>`);
    else if (tok.startsWith('`')) parts.push(html`<code class="px-1 py-0.5 rounded bg-base-300 text-xs">${tok.slice(1, -1)}</code>`);
    else {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm && /^https?:\/\//i.test(lm[2])) parts.push(html`<a href=${lm[2]} target="_blank" rel="noopener noreferrer">${lm[1]}</a>`);
      else parts.push(tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
const splitCells = (line: string) => {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
};
const isSeparatorRow = (cells: string[]) => cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c.replace(/\s+/g, '')));

function renderMarkdown(md: string) {
  const blocks: unknown[] = [];
  let bullets: unknown[] = [];
  let tableRows: string[] = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(html`<ul class="list-disc ml-5 my-1 space-y-1">${bullets}</ul>`);
      bullets = [];
    }
  };
  const flushTable = () => {
    if (!tableRows.length) return;
    const rows = tableRows.map(splitCells);
    tableRows = [];
    const header = rows[0];
    const bodyStart = rows[1] && isSeparatorRow(rows[1]) ? 2 : 1;
    const body = rows.slice(bodyStart);
    blocks.push(html`
      <table class="np-table">
        <thead>
          <tr>${header.map((c) => html`<th>${renderInline(c)}</th>`)}</tr>
        </thead>
        <tbody>
          ${body.map((r) => html`<tr>${r.map((c) => html`<td>${renderInline(c)}</td>`)}</tr>`)}
        </tbody>
      </table>`);
  };
  for (const raw of md.split('\n')) {
    const t = raw.replace(/\r$/, '').trim();
    if (t.startsWith('|') && t.length > 1) { flush(); tableRows.push(t); continue; }
    flushTable();
    if (t === '') { flush(); continue; }
    if (t.startsWith('## ')) { flush(); blocks.push(html`<h3 class="font-semibold text-base mt-2 mb-1">${renderInline(t.slice(3))}</h3>`); continue; }
    if (t.startsWith('# ')) { flush(); blocks.push(html`<h3 class="font-semibold text-base mt-2 mb-1">${renderInline(t.slice(2))}</h3>`); continue; }
    const bm = t.match(/^[-*]\s+(.*)$/);
    if (bm) { bullets.push(html`<li>${renderInline(bm[1])}</li>`); continue; }
    flush();
    blocks.push(html`<p class="my-1">${renderInline(t)}</p>`);
  }
  flush();
  flushTable();
  return blocks;
}

const FOLDER_DESC: Record<string, string> = {
  raw: 'unstructured sources',
  wiki: "the agent's distilled memory",
  output: 'archived reports & deliverables',
};

export const VaultStudioPage = stateful(() => {
  const [vault, setVault] = useState<VaultTree | null>(null);
  const [openFile, setOpenFile] = useState<{ folder: string; name: string; content: string } | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    void (async () => {
      try {
        setVault(JSON.parse(await listVaultTree()()));
      } catch {
        /* ignore */
      }
      setLoaded(true);
    })();
  }, []);

  const open = async (folder: string, name: string) => {
    setOpenFile({ folder, name, content: '…' });
    try {
      const content: string = await readVaultDoc(folder, name)();
      setOpenFile({ folder, name, content: content || '(empty file)' });
    } catch (e: any) {
      setOpenFile({ folder, name, content: '⚠ ' + String(e?.message ?? e) });
    }
  };

  const fileLink = (folder: string, name: string, label?: string) => html`
    <li>
      <button
        class="w-full text-left text-sm py-1 px-2 rounded transition-colors hover:bg-amber-900/10 ${openFile?.folder === folder && openFile?.name === name ? 'bg-amber-900/10 font-semibold' : ''}"
        style="color:#4a3416; font-family:'Old Standard TT',Georgia,serif;"
        @click=${() => open(folder, name)}
      >
        ${label ?? name}
      </button>
    </li>
  `;

  return html`
    ${newspaperStyle(NP.PAPER_DARK)}
    <div class="np-wrap">
      <div class="container mx-auto px-4 py-6 max-w-6xl">
        ${masthead('Vault Studio', "The archive — the agent's memory")}

        ${vault
          ? html`
              <div class="np-card rounded-lg mb-5">
                <div class="p-5 text-sm" style="font-family:'Old Standard TT',Georgia,serif;">${renderMarkdown(vault.rootIndex || '(no vault/index.md)')}</div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                ${vault.folders.map(
                  (f) => html`
                    <div class="np-card rounded-lg">
                      <div class="p-5">
                        <div class="flex items-baseline gap-2 pb-2 mb-2" style="border-bottom:1px solid rgba(40,30,18,.3);">
                          <span class="font-bold text-2xl" style="font-family:'Playfair Display',Georgia,serif;">/${f.name}</span>
                          <span class="text-xs italic ml-auto" style="color:#6b5c44;">${f.files.length} files</span>
                        </div>
                        <p class="text-xs italic mb-3" style="color:#6b5c44;">${FOLDER_DESC[f.name] ?? ''}</p>
                        <ul class="space-y-0.5">
                          ${fileLink(f.name, 'index.md', 'index.md — about this folder')}
                          ${f.files.map((name) => fileLink(f.name, name))}
                        </ul>
                      </div>
                    </div>
                  `,
                )}
              </div>

              ${openFile
                ? html`
                    <div class="np-card rounded-lg">
                      <div class="p-6">
                        <div class="flex items-center gap-2 mb-2 pb-2" style="border-bottom:1px solid rgba(40,30,18,.3);">
                          <span class="font-semibold" style="font-family:'Playfair Display',serif;">/${openFile.folder}/${openFile.name}</span>
                          <button class="btn btn-ghost btn-xs ml-auto" @click=${() => setOpenFile(null)}>close</button>
                        </div>
                        <div class="text-sm leading-relaxed" style="font-family:'Old Standard TT',Georgia,serif;">${renderMarkdown(openFile.content)}</div>
                      </div>
                    </div>
                  `
                : html`<p class="text-center italic py-6" style="color:#6b5c44;">Click any file to read it.</p>`}
            `
          : html`<div class="py-4" style="color:#6b5c44;">${loaded ? 'No vault found.' : 'Loading…'}</div>`}
      </div>
    </div>
  `;
});
